import { Search } from "lucide-react";
import { StatusPill } from "../../../ui";
import type { SeoFacts } from "../../../../lib/siteScan";
import type { T } from "../types";
import { Field, LengthMeter, Panel } from "./primitives";

/** Findability: the fields a rewrite fixes first, with their lengths measured. */
export function SeoPanel({ seo, t }: { seo: SeoFacts; t: T }) {
  return (
    <Panel icon={Search} title={t("scanner.seo.title")}>
      <div className="space-y-3">
        <div>
          <dt className="text-[11px] font-medium text-muted-foreground">
            {t("scanner.seo.pageTitle")}
            <LengthMeter length={seo.titleLength} min={15} max={65} />
          </dt>
          <dd className="text-sm">{seo.title ?? "-"}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-medium text-muted-foreground">
            {t("scanner.seo.description")}
            <LengthMeter length={seo.descriptionLength} min={50} max={160} />
          </dt>
          <dd className="text-sm text-muted-foreground">{seo.description ?? "-"}</dd>
        </div>

        <dl className="grid grid-cols-2 gap-3 border-t border-[var(--crm-hair)] pt-3 sm:grid-cols-4">
          <Field label={t("scanner.seo.headings")} value={`H1 ${seo.headingCounts.h1} / H2 ${seo.headingCounts.h2}`} />
          <Field label={t("scanner.seo.lang")} value={seo.lang ?? "-"} />
          <Field
            label={t("scanner.seo.images")}
            value={t("scanner.seo.imagesValue", { total: seo.images, missing: seo.imagesWithoutAlt })}
          />
          <Field label={t("scanner.seo.words")} value={String(seo.wordCount)} />
          <Field label={t("scanner.seo.copyright")} value={seo.copyrightYear == null ? "-" : String(seo.copyrightYear)} />
          <Field
            label={t("scanner.seo.lastmod")}
            value={seo.sitemapLastMod ? new Date(seo.sitemapLastMod).toLocaleDateString("et-EE") : "-"}
          />
          <Field label={t("scanner.seo.robots")} value={t(seo.robotsTxt ? "common.yes" : "common.no")} />
          <Field label={t("scanner.seo.favicon")} value={t(seo.favicon ? "common.yes" : "common.no")} />
        </dl>

        {seo.structuredData.length > 0 && (
          <div className="border-t border-[var(--crm-hair)] pt-3">
            <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">{t("scanner.seo.structured")}</p>
            <div className="flex flex-wrap gap-1.5">
              {seo.structuredData.map((type) => (
                <StatusPill key={type} tone="mut">
                  {type}
                </StatusPill>
              ))}
            </div>
          </div>
        )}

        {seo.hreflang.length > 0 && (
          <div className="border-t border-[var(--crm-hair)] pt-3">
            <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">{t("scanner.seo.hreflang")}</p>
            <div className="flex flex-wrap gap-1.5">
              {seo.hreflang.map((lang) => (
                <StatusPill key={lang} tone="mut" className="uppercase">
                  {lang}
                </StatusPill>
              ))}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}
